import type React from 'react';
import { useState } from 'react';
import { fetchAccountInfoById, fetchRentalCompanies } from '../../../api/api';
import classNames from 'classnames';
import { useAppDispatch, useAppSelector } from '../../../../configuration/setup/hooks.ts';
import { getRentalCompanies, setRentalCompanies } from '../../appSlice.ts';

export const AccountIdWithName = ({ accountId }: { accountId: string }) => {
    const [accountName, setAccountName] = useState('');
    const [accountType, setAccountType] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const rentalCompanies = useAppSelector(getRentalCompanies);
    const dispatch = useAppDispatch();

    const isRentalCompany = rentalCompanies.map(it => it.accountId).includes(accountId);

    const onClick = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (isLoading) {
            return;
        }

        setIsLoading(true);
        fetchAccountInfoById(accountId)
            .then(res => {
                setAccountName(res.accountName);
                setAccountType(res.accountType);
            })
            .catch((err: unknown) => {
                console.error('Could not resolve account name:', err);
            })
            .finally(() => setIsLoading(false));

        if (rentalCompanies.length === 0) {
            fetchRentalCompanies()
                .then(res => dispatch(setRentalCompanies(res)))
                .catch((err: unknown) => {
                    console.error('Could not resolve account name:', err);
                });
        }
    };

    return (
        <>
            {accountName ? (
                <table className={'table table-condensed text-color-black'}>
                    <tbody>
                        <tr>
                            <td className={'padding-0 padding-bottom-5'} style={{ borderTop: 0 }}>
                                {accountId}
                            </td>
                        </tr>
                        <tr>
                            <td className={'padding-0 text-color-dark'} style={{ borderTop: 0 }}>
                                <AccountTypeIcon accountType={accountType} isRentalCompany={isRentalCompany} />{' '}
                                {accountName}
                            </td>
                        </tr>
                    </tbody>
                </table>
            ) : (
                <div>
                    {/* biome-ignore lint/a11y/useValidAnchor: suppress out of convenience during migration to biome */}
                    <a href={'#'} className={'text-color-black'} onClick={onClick} style={{ textDecoration: 'none' }}>
                        {accountId}
                    </a>
                    {isLoading ? (
                        <span className={'btn btn-muted btn-loading btn-icon-only'} />
                    ) : (
                        <span className={'btn btn-muted btn-loading btn-icon-only'} style={{ visibility: 'hidden' }} />
                    )}
                </div>
            )}
        </>
    );
};

interface AccountTypeIconProps {
    accountType: string;
    isRentalCompany: boolean;
}

const glyphFrom = (accountType: string, isRentalCompany: boolean): string | null => {
    if (accountType === 'PARTNER_CARRIER') {
        return 'rioglyph-businessman';
    } else if (isRentalCompany) {
        return 'rioglyph-share-nodes';
    } else {
        return null;
    }
};

const titleFrom = (accountType: string, isRentalCompany: boolean): string | null => {
    if (accountType === 'PARTNER_CARRIER') {
        return 'Partner Carrier';
    } else if (isRentalCompany) {
        return 'Rental Company';
    } else {
        return null;
    }
};

const AccountTypeIcon = (props: AccountTypeIconProps) => {
    const glyph = glyphFrom(props.accountType, props.isRentalCompany);
    const title = titleFrom(props.accountType, props.isRentalCompany);

    if (!glyph || !title) {
        return <></>;
    }

    return <span className={classNames('rioglyph', glyph, 'rioglyph-align-baseline')} title={title} />;
};
